import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function SantanderPixConfigPanel() {
  const [cfg, setCfg] = useState({
    environment: 'sandbox', client_id: '', client_secret: '', pix_key: '',
    webhook_url: `${window.location.origin}/api/payments/santander/webhook`,
    cob_expiration: 3600, use_fiscal_a1_cert: true, pix_enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/payments/santander/admin/config`, auth()),
      axios.get(`${API}/api/payments/santander/admin/status`, auth()).catch(() => ({ data: {} })),
    ])
      .then(([c, s]) => { setCfg(p => ({ ...p, ...c.data, client_secret: '' })); setStatus(s.data); })
      .catch(e => setMsg({ type: 'error', text: e.response?.data?.detail || e.message }))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      if (!cfg.client_secret && !cfg.client_secret_set) {
        setMsg({ type: 'error', text: 'Client Secret e obrigatorio na 1a configuracao' }); return;
      }
      const p = { ...cfg }; if (!p.client_secret) delete p.client_secret;
      await axios.post(`${API}/api/payments/santander/admin/config`, p, auth());
      setMsg({ type: 'success', text: 'Configuracao salva. Agora teste a conexao.' });
      setCfg(prev => ({ ...prev, client_secret: '', client_secret_set: true }));
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/api/payments/santander/admin/test`, {}, auth());
      setTestResult(data);
    } catch (e) { setTestResult({ ok: false, error: e.response?.data?.detail || e.message }); }
    finally { setTesting(false); }
  };

  const registerWebhook = async () => {
    setMsg(null);
    try {
      const { data } = await axios.post(`${API}/api/payments/santander/admin/register-webhook`, {}, auth());
      if (data.success) setMsg({ type: 'success', text: 'Webhook registrado no Santander!' });
      else setMsg({ type: 'error', text: data.error || 'Falha ao registrar webhook' });
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || e.message }); }
  };

  if (loading) return <div style={{ padding: 20 }}>Carregando…</div>;

  const cardStyle = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 };
  const label = { display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 };
  const input = { width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' };
  const grid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 };

  return (
    <div data-testid="santander-config-panel" style={{ maxWidth: 820 }}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 8, background: '#ec0000', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 22 }}>S</div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>PIX Santander</h3>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Integracao BACEN oficial (mTLS + OAuth2)</p>
          </div>
          {status?.configured && (
            <span style={{ background: '#dcfce7', color: '#16a34a', padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>✓ Configurado</span>
          )}
        </div>
      </div>

      <div style={cardStyle}>
        <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700 }}>Credenciais</h4>
        <div style={grid}>
          <div>
            <label style={label}>Ambiente</label>
            <select data-testid="sant-env" style={input} value={cfg.environment}
              onChange={e => setCfg({ ...cfg, environment: e.target.value })}>
              <option value="sandbox">Sandbox (homologacao)</option>
              <option value="production">Producao</option>
            </select>
          </div>
          <div>
            <label style={label}>PIX ativo no cardapio</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 0', cursor: 'pointer' }}>
              <input data-testid="sant-enabled" type="checkbox" checked={cfg.pix_enabled}
                onChange={e => setCfg({ ...cfg, pix_enabled: e.target.checked })} />
              <span>Habilitado</span>
            </label>
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={label}>Client ID <span style={{ color: '#dc2626' }}>*</span></label>
          <input data-testid="sant-client-id" style={input} value={cfg.client_id}
            onChange={e => setCfg({ ...cfg, client_id: e.target.value })} placeholder="Ex: abc123..." />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={label}>Client Secret {cfg.client_secret_set ? <span style={{ color: '#64748b', fontWeight: 400 }}>(vazio = mantem atual)</span> : <span style={{ color: '#dc2626' }}>*</span>}</label>
          <input data-testid="sant-secret" type="password" style={input} value={cfg.client_secret}
            onChange={e => setCfg({ ...cfg, client_secret: e.target.value })}
            placeholder={cfg.client_secret_set ? '••••••••••••' : 'Cole aqui'} />
        </div>
        <div style={grid}>
          <div>
            <label style={label}>Chave PIX no Santander <span style={{ color: '#dc2626' }}>*</span></label>
            <input data-testid="sant-pixkey" style={input} value={cfg.pix_key}
              onChange={e => setCfg({ ...cfg, pix_key: e.target.value })}
              placeholder="CNPJ / e-mail / chave aleatoria" />
          </div>
          <div>
            <label style={label}>Expiracao (segundos)</label>
            <input data-testid="sant-exp" type="number" style={input} value={cfg.cob_expiration}
              onChange={e => setCfg({ ...cfg, cob_expiration: Number(e.target.value) || 3600 })} />
          </div>
        </div>
        <div>
          <label style={label}>URL Webhook (publico HTTPS)</label>
          <input data-testid="sant-webhook" style={input} value={cfg.webhook_url}
            onChange={e => setCfg({ ...cfg, webhook_url: e.target.value })} />
        </div>
      </div>

      <div style={cardStyle}>
        <h4 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700 }}>Certificado A1 (mTLS)</h4>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: 12,
          border: '1px solid #cbd5e1', borderRadius: 8,
          background: cfg.use_fiscal_a1_cert ? '#f0fdf4' : '#fff' }}>
          <input data-testid="sant-use-fiscal" type="checkbox" checked={cfg.use_fiscal_a1_cert}
            style={{ marginTop: 2 }}
            onChange={e => setCfg({ ...cfg, use_fiscal_a1_cert: e.target.checked })} />
          <div>
            <div style={{ fontWeight: 600 }}>Reaproveitar cert A1 do modulo Fiscal (SEFAZ)</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              O mesmo .pfx da NFCE sera usado como certificado mTLS do Santander. Zero upload duplicado.
            </div>
          </div>
        </label>
      </div>

      {msg && (
        <div style={{ padding: 12, borderRadius: 8, marginBottom: 14,
          background: msg.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: msg.type === 'success' ? '#15803d' : '#b91c1c', fontSize: 13 }}>{msg.text}</div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button data-testid="sant-save" onClick={save} disabled={saving}
          style={{ padding: '11px 24px', borderRadius: 8, border: 'none', background: '#ec0000', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14, opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Salvando…' : 'Salvar'}
        </button>
        <button data-testid="sant-test" onClick={testConnection} disabled={testing || !cfg.client_id}
          style={{ padding: '11px 20px', borderRadius: 8, border: '1px solid #ec0000', background: '#fff', color: '#ec0000', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
          {testing ? 'Testando…' : 'Testar conexao'}
        </button>
        <button data-testid="sant-register" onClick={registerWebhook}
          style={{ padding: '11px 20px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
          Registrar webhook
        </button>
      </div>

      {testResult && (
        <div style={{ marginTop: 14, padding: 12, borderRadius: 8,
          background: testResult.ok ? '#dcfce7' : '#fee2e2',
          color: testResult.ok ? '#15803d' : '#b91c1c', fontSize: 13, fontFamily: 'monospace' }}>
          {testResult.ok ? `✓ Autenticacao OK. Token: ${testResult.token_prefix}` : `✗ ${testResult.error}`}
        </div>
      )}
    </div>
  );
}
